import React from 'react';

import { BookOpen } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import {
  Card,
  Heading,
  useToken,
  Box,
  HStack,
  VStack,
  Center,
  ButtonText,
  Button,
  Icon,
} from '@gluestack-ui/themed';
import { Image as ExpoImage } from 'expo-image';
import ModuleCardLecturer from './ModuleCardLecturer';
import HtmlText from '../../../common/components/HtmlText';
import LabelValueBadge from '../../../common/components/LabelValueBadge';

const SelectedModuleCard = ({
  title,
  description,
  levels,
  imageUrl,
  lecturer,
  color,
  handleWorkOnModule,
  grade,
  isGraded,
  isSubmitted,
  testID,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Memoize the image source to prevent unnecessary re-renders and handle fallback
  const imageSource = React.useMemo(() => (imageUrl
    ? { uri: imageUrl }
    : require('../../../assets/img/fallback-module-image.jpg')), [imageUrl]);

  // Generate learning objectives from levels in form of level headings and unordered lists per level
  const learningObjectives = React.useMemo(() => {
    if (!levels) return [];
    return levels.map((level) => `<b>Level ${level.id}: ${level.title}</b><ul>${level.objectives.map((objective) => `<li>${objective}</li>`).join('\n')}</ul>`).join('\n');
  }, [levels]);

  const moduleColor = useToken('colors', `${color}400`);

  if (isSubmitted || isGraded) return null;
  return (
    <Card
      testID={testID}
      $lg-width={800}
      $base-minHeight={480}
      $sm-minHeight={546}
      borderRadius={12}
      p={0}
      mb={20}
      pb="$4"
      bg={moduleColor}
    >
      <Box
        borderTopLeftRadius={12}
        borderTopRightRadius={12}
        width="100%"
        height={240}
      >
        <ExpoImage
          source={imageSource}
          alt="Module Image"
          style={{
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            width: '100%',
            height: 240,
          }}
          contentFit="cover"
          cachePolicy="memory-disk"
          transition={0}
        />

        <Box position="absolute" right={0} mr={28} top={28}>
          <HStack space="sm">
            {isGraded && <LabelValueBadge label="GRADE" value={grade} />}
            {isSubmitted && !isGraded && <LabelValueBadge label="SUBMITTED" value="PENDING" color="$statusInfo" />}
            <ModuleCardLecturer lecturer={lecturer} />
          </HStack>
        </Box>
      </Box>

      <VStack width="100%" px={28} $base-py={12} $sm-py={28}>
        {/* Bar under Image: Icon + Progress */}

        <HStack justifyContent="space-between" alignItems="flex-start">
          <Center
            width={40}
            height={40}
            borderRadius={8}
            bg="white"
            p={10}
            $base-display="none"
            $sm-display="flex"
          >
            <Icon as={BookOpen} color="$textHeading" size="lg" />
          </Center>
        </HStack>

        {/* Content: Title, Description */}

        <VStack my={24}>
          <Heading
            testID="current-module-title"
            size="lg"
            lineHeight="$lg"
            numberOfLines={isExpanded ? null : 2}
            mb="$2"
          >
            {title}
          </Heading>
          <Box overflow="hidden">
            <HtmlText
              testID="current-module-description"
              content={description}
              fontSize={14}
              numberOfLines={isExpanded ? null : 4}
            />
          </Box>
          <Box
            testID="current-module-learning-objectives-box"
            display={isExpanded ? 'flex' : 'none'}
            overflow="hidden"
            w="90%"
          >
            <HtmlText
              content={learningObjectives}
              fontSize={14}
            />
          </Box>
          <Button
            variant="link"
            testID="current-module-show-more-toggle"
            justifyContent="flex-start"
            size="xs"
            onPress={() => setIsExpanded(!isExpanded)}
          >
            <ButtonText color="$textHeading" size="2xs">
              {isExpanded
                ? t('common.buttons.show-less')
                : t('common.buttons.show-more')}
            </ButtonText>
          </Button>
        </VStack>

        {/* ButtonBar: Close, Work */}

        <HStack space="lg" alignSelf="flex-end">
          <Button
            testID="current-module-work-on-module-button"
            variant="solid"
            action="primary"
            size="sm"
            onPress={handleWorkOnModule}
          >
            <ButtonText>
              {t('student.course.modulecard.button-workonmodule')}
            </ButtonText>
          </Button>
        </HStack>
      </VStack>
    </Card>
  );
};

export default SelectedModuleCard;
